"use client"
import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"

interface GHLBookingWidgetProps {
  src: string
  id: string
  title: string
  defaultHeight?: number
  borderRadius?: number
  border?: string
  background?: string
  /** Redirect to this path after a booking is confirmed (e.g. "/thank-you") */
  redirectTo?: string
  /** Defer setting the iframe src until after mount (fixes GHL script timing issues) */
  deferLoad?: boolean
  /**
   * Load nothing (no iframe src, no form_embed.js) until the widget is within
   * `lazyMargin` of the viewport. ONLY for placements far below the fold — the
   * homepage puts this calendar at the very bottom of a long page, and eager-loading
   * it there cost ~545 KB of GHL bundle + GHL's own Meta pixel on every homepage
   * visit (LCP 5.0 s on mobile, 2026-09-01 audit). On /booking and /contact the
   * widget IS the page and must stay eager — an earlier in-view gate there left the
   * frame mounted with no src. Do not enable this on those pages.
   */
  lazy?: boolean
  /** Distance from the viewport at which a `lazy` widget starts loading. */
  lazyMargin?: string
}

// Origins allowed to confirm a booking: the widget itself (white-labelled
// link.zip360.co.za) plus GHL's own domains, in case a nested frame posts it.
const CONFIRMATION_ORIGIN_SUFFIXES = [".zip360.co.za", ".msgsndr.com", ".leadconnectorhq.com"] as const

export function isTrustedBookingOrigin(origin: string): boolean {
  let host: string
  try {
    host = new URL(origin).hostname
  } catch {
    return false
  }
  return CONFIRMATION_ORIGIN_SUFFIXES.some(s => host.endsWith(s) || host === s.slice(1))
}

// Only messages that unambiguously mean "an appointment slot was actually booked".
// Loose signals (keyword matching on strings, formSubmitted/formSubmit) redirected
// visitors to /thank-you before they had picked a date — the widget and
// form_embed.js post such messages during normal loading and step navigation.
export function isBookingConfirmation(data: unknown): boolean {
  if (!data) return false

  // ✅ Confirmed GHL/LeadConnector event: array where [0] === 'msgsndr-booking-complete'
  if (Array.isArray(data) && data[0] === "msgsndr-booking-complete") return true

  if (typeof data !== "object" || Array.isArray(data)) return false
  const d = data as Record<string, unknown>

  return (
    d.event === "booking_confirmed" ||
    d.event === "appointmentBooked" ||
    d.event === "appointment_booked" ||
    d.type === "appointment_booked" ||
    d.type === "appointmentBooked" ||
    d.action === "appointmentBooked" ||
    d.action === "booking_confirmed" ||
    d.message === "booking_confirmed" ||
    d.booked === true ||
    d.isBooked === true
  )
}

// The height GHL's widget reports for its own content, or null if the message
// is not a height message. The live widget posts the ARRAY form
// ["highlevel.setHeight", { height, id: "msgsndr-calendar" }] (verified on
// /booking 2026-09-08: 1021 px desktop, 805 px mobile). The older object /
// bare-number forms are kept because form_embed.js has used them before.
export function parseReportedHeight(data: unknown): number | null {
  if (data == null) return null
  const ok = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n) && n > 200

  if (Array.isArray(data)) {
    if (data[0] !== "highlevel.setHeight") return null
    const payload = data[1]
    if (payload && typeof payload === "object" && ok((payload as Record<string, unknown>).height)) {
      return (payload as Record<string, unknown>).height as number
    }
    return ok(payload) ? payload : null
  }
  if (ok(data)) return data
  if (typeof data !== "object") return null
  const d = data as Record<string, unknown>
  if (d.action === "setHeight" && ok(d.height)) return d.height
  if (d.type === "setHeight" && ok(d.value)) return d.value
  if (ok(d.height)) return d.height
  return null
}

export function GHLBookingWidget({
  src,
  id,
  title,
  defaultHeight = 800,
  borderRadius = 24,
  border = "1px solid rgba(255,255,255,0.08)",
  background = "rgba(255,255,255,0.03)",
  redirectTo,
  deferLoad = false,
  lazy = false,
  lazyMargin = "1200px",
}: GHLBookingWidgetProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(defaultHeight)
  // Eager widgets are "near" from the first render; lazy ones wait for the observer.
  const [near, setNear] = useState(!lazy)
  const router = useRouter()

  useEffect(() => {
    if (!lazy || near) return
    const el = wrapRef.current
    if (!el) return
    if (typeof IntersectionObserver === "undefined") {
      // No observer support (very old WebKit): load on the next tick rather than never.
      const t = setTimeout(() => setNear(true), 0)
      return () => clearTimeout(t)
    }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { setNear(true); io.disconnect() }
    }, { rootMargin: lazyMargin })
    io.observe(el)
    return () => io.disconnect()
  }, [lazy, near, lazyMargin])

  // Assign on mount, NOT on in-view. Gating this behind IntersectionObserver was an
  // audit-score optimisation and it cost correctness: on /booking the frame mounted and
  // never received a src, and on /contact the form intermittently failed to appear.
  // A booking form that sometimes doesn't load is worth far more than the Lighthouse
  // third-party-cookie point that gating it bought. Restored to the original behaviour.
  useEffect(() => {
    if (!near) return
    if (deferLoad && iframeRef.current && !iframeRef.current.src) {
      iframeRef.current.src = src
    }
  }, [deferLoad, src, near])

  // form_embed.js is what sizes this iframe (it posts the height back, which the
  // message handler below applies). The widget must never depend on the visitor
  // interacting first: on /contact and /booking the iframe is at the top of the page,
  // so gating the script behind interaction left the frame mounted but unsized —
  // the form "sometimes doesn't load on the first try". Whenever a widget goes
  // in-view we guarantee the script, independently of GHLFormScript's lazy path.
  // Idempotent: one tagged script per document, no matter how many widgets mount.
  useEffect(() => {
    if (!near) return
    const SRC = "https://link.zip360.co.za/js/form_embed.js"
    if (document.querySelector(`script[data-ghl-form-embed]`)) return
    const s = document.createElement("script")
    s.src = SRC
    s.async = true
    s.setAttribute("data-ghl-form-embed", "")
    s.setAttribute("data-cmp-ab", "off")
    document.body.appendChild(s)
  }, [near])

  // Sync container height when form_embed.js directly sets iframe inline style height
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return
    const mo = new MutationObserver(() => {
      const inlineH = parseFloat(iframe.style.height)
      if (!isNaN(inlineH) && inlineH > 200) {
        setHeight(inlineH)
        iframe.style.height = "100%"
      }
    })
    mo.observe(iframe, { attributes: true, attributeFilter: ["style"] })
    return () => mo.disconnect()
  }, [])

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (!e.data) return

      try {
        const raw = e.data
        const data = typeof raw === "string" ? (() => { try { return JSON.parse(raw) } catch { return raw } })() : raw

        // Booking confirmed → redirect after 5 s so user sees GHL confirmation screen.
        // Origin-gated: only the booking widget's own frames may trigger it.
        if (redirectTo && isTrustedBookingOrigin(e.origin) && isBookingConfirmation(data)) {
          setTimeout(() => router.push(redirectTo), 5000)
          return
        }

        // Height adjustments: follow the widget's own content height exactly, so the
        // frame never scrolls internally and never leaves dead space beneath the
        // calendar. defaultHeight is only the placeholder before the first report.
        const reported = parseReportedHeight(data)
        if (reported !== null) {
          setHeight(reported)
          return
        }
      } catch {
        // ignore
      }
    }

    // Capture phase — fires before form_embed.js bubble-phase listeners
    window.addEventListener("message", handleMessage, true)
    return () => window.removeEventListener("message", handleMessage, true)
  }, [defaultHeight, redirectTo, router])

  return (
    <div
      ref={wrapRef}
      style={{
        border,
        borderRadius,
        background,
        width: "100%",
        height,
        transition: "height 0.3s ease",
        overflow: "visible",
        position: "relative",
      }}
    >
      <iframe
        ref={iframeRef}
        data-cmp-ab="off"
        src={deferLoad || !near ? undefined : src}
        id={id}
        title={title}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block",
          borderRadius,
        }}
        allow="camera; microphone; autoplay; encrypted-media; fullscreen"
      />
    </div>
  )
}
